import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getLogo } from "./logo";
import { BRAND_TXT } from "./brand";

let brandLogo = null;
fetch("/img/farmanaliza-logo.png").then((r) => r.blob())
  .then((b) => new Promise((res) => { const f = new FileReader(); f.onload = () => res(f.result); f.readAsDataURL(b); }))
  .then((d) => { brandLogo = d; }).catch(() => {});

/**
 * Generic table export utilities.
 *
 *   exportToExcel({ filename, sheetName, columns, rows })
 *   exportToPdf({ filename, title, subtitle, columns, rows })
 *
 * `columns` is an array of: { header, key, format?, align? }
 *   - format: optional (value) => formatted display string
 *   - align: "left" | "center" | "right"  (PDF only)
 */

function _row(columns, item) {
  return columns.map((c) => {
    const v = typeof c.key === "function" ? c.key(item) : item?.[c.key];
    return c.format ? c.format(v, item) : v;
  });
}

export function exportToExcel({ filename, sheetName = "Datos", columns, rows }) {
  const headers = columns.map((c) => c.header);
  const data = rows.map((r) => _row(columns, r));
  const aoa = [headers, ...data, [], [`Farmanaliza · ${BRAND_TXT}`]];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Auto-size columns
  const colWidths = columns.map((c, i) => {
    const maxLen = Math.max(
      String(c.header).length,
      ...data.slice(0, 200).map((r) => String(r[i] ?? "").length)
    );
    return { wch: Math.min(Math.max(maxLen + 2, 8), 50) };
  });
  ws["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 30));
  XLSX.writeFile(wb, ensureExt(filename, "xlsx"));
}

export const PDF_MAX_ROWS = 500;

export function exportToPdf({ filename, title, subtitle, columns, rows, orientation, totalRows }) {
  const total = totalRows ?? rows.length;
  if (total > PDF_MAX_ROWS) {
    const note = `Se muestran los ${PDF_MAX_ROWS} primeros de ${total.toLocaleString("es-ES")} (el Excel incluye todos)`;
    subtitle = subtitle ? `${subtitle} · ${note}` : note;
    rows = rows.slice(0, PDF_MAX_ROWS);
  }
  columns = columns.map((c) => (c.pdf ? { ...c, format: c.pdf } : c));
  const auto = orientation || (columns.length > 8 ? "landscape" : "portrait");
  const doc = new jsPDF({ orientation: auto, unit: "pt", format: "a4" });
  const logo = getLogo();
  if (logo.dataUrl) {
    const h = 34;
    const w = Math.min(140, h * (logo.ratio || 1));
    try { doc.addImage(logo.dataUrl, doc.internal.pageSize.getWidth() - 40 - w, 22, w, w / (logo.ratio || 1)); } catch { /* formato no soportado */ }
  }
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(title || filename, 40, 40);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120);
    doc.text(subtitle, 40, 58);
    doc.setTextColor(0);
  }
  doc.setFontSize(8);
  doc.text(`Generado el ${new Date().toLocaleString("es-ES")}`, 40, subtitle ? 72 : 58);

  autoTable(doc, {
    margin: { bottom: 34 },
    startY: subtitle ? 84 : 70,
    head: [columns.map((c) => c.header)],
    body: rows.map((r) => _row(columns, r).map((v) => (v == null ? "" : String(v)))),
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: "bold", fontSize: 8 },
    bodyStyles: { fontSize: 7 },
    styles: { cellPadding: 3, overflow: "linebreak" },
    columnStyles: columns.reduce((acc, c, i) => {
      if (c.align) acc[i] = { halign: c.align };
      return acc;
    }, {}),
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      const page = data.pageNumber;
      doc.setFontSize(7);
      doc.setTextColor(150);
      const H = doc.internal.pageSize.getHeight();
      const W = doc.internal.pageSize.getWidth();
      const x = data.settings.margin.left;
      if (brandLogo) { try { doc.addImage(brandLogo, "PNG", x, H - 24, 46, 14, "farmanaliza-brand"); } catch { /* sin logo */ } }
      doc.text(BRAND_TXT, x + (brandLogo ? 52 : 0), H - 14);
      doc.text(`Página ${page} de ${pageCount}`, W - data.settings.margin.right, H - 14, { align: "right" });
    },
  });
  doc.save(ensureExt(filename, "pdf"));
}

function ensureExt(filename, ext) {
  if (filename.toLowerCase().endsWith("." + ext)) return filename;
  return `${filename}.${ext}`;
}

/**
 * Convenience React component: <ExportButtons columns={...} rows={...} filename="..." />
 */
import React from "react";
import { FileSpreadsheet, FileText } from "lucide-react";
export function ExportButtons({ filename, title, subtitle, columns, rows, sheetName, orientation, className = "", size = "sm", testIdPrefix = "export" }) {
  const disabled = !rows || rows.length === 0;
  return (
    <div className={"inline-flex gap-2 " + className}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => exportToExcel({ filename, sheetName, columns, rows })}
        className={"btn btn-outline btn-" + size}
        data-testid={`${testIdPrefix}-excel-button`}
        title={`Exportar ${rows?.length || 0} filas a Excel`}
      >
        <FileSpreadsheet size={14} /> Excel
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => exportToPdf({ filename, title: title || filename, subtitle, columns, rows, orientation })}
        className={"btn btn-outline btn-" + size}
        data-testid={`${testIdPrefix}-pdf-button`}
        title={`Exportar ${rows?.length || 0} filas a PDF`}
      >
        <FileText size={14} /> PDF
      </button>
    </div>
  );
}
